"use client";
/**
 * React Query over the share routes. Keys carry the signed-in user, so two accounts in
 * one tab never read each other's cache. Mutations answer a ShareCall (they never
 * throw); the dialog reads `.ok` and the refusal code.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AddPersonResult,
  GeneralAccess,
  SharePerson,
  ShareState,
  SharedWithMeItem,
  SuggestedPerson,
} from "@bash/shared";
import { useAuth } from "@/contexts/AuthContext";
import {
  addPersonCall,
  fetchShare,
  fetchShareMarks,
  fetchSharedWithMe,
  fetchShareSuggestions,
  removePersonCall,
  setGeneralAccessCall,
  type ShareCall,
  type ShareRefusal,
} from "./api";

export const shareKeys = {
  state: (uid: string | null, id: string) => ["share", uid, id] as const,
  withMe: (uid: string | null) => ["shares-with-me", uid] as const,
  marks: (uid: string | null) => ["share-marks", uid] as const,
  suggestions: (uid: string | null) => ["share-suggestions", uid] as const,
};

export class ShareLoadError extends Error {
  constructor(readonly code: ShareRefusal) {
    super(code);
  }
}

const useUid = (): string | null => useAuth().user?.id ?? null;

async function unwrap<T>(p: Promise<ShareCall<T>>): Promise<T> {
  const r = await p;
  if (!r.ok) throw new ShareLoadError(r.code);
  return r.value;
}

export function useShareState(id: string | null) {
  const uid = useUid();
  return useQuery<ShareState, ShareLoadError>({
    queryKey: shareKeys.state(uid, id ?? ""),
    enabled: !!uid && !!id,
    queryFn: () => unwrap(fetchShare(id!)),
    retry: (n, err) => err.code !== "not_found" && n < 2,
  });
}

export function useSharedWithMe() {
  const uid = useUid();
  return useQuery<SharedWithMeItem[], ShareLoadError>({
    queryKey: shareKeys.withMe(uid),
    enabled: !!uid,
    queryFn: async () => (await unwrap(fetchSharedWithMe())).items,
  });
}

/**
 * How many people each of this owner's files and creations is shared with, or undefined
 * until the counts have loaded (and after a failure). Unknown is never "nobody": a file
 * missing from a LOADED record is shared with no one, a file read before it is not known.
 */
export function useShareMarks(): Record<string, number> | undefined {
  const uid = useUid();
  const q = useQuery({
    queryKey: shareKeys.marks(uid),
    enabled: !!uid,
    queryFn: async () => (await unwrap(fetchShareMarks())).marks,
  });
  return q.data;
}

export function useShareSuggestions(enabled: boolean): SuggestedPerson[] {
  const uid = useUid();
  const q = useQuery({
    queryKey: shareKeys.suggestions(uid),
    enabled: enabled && !!uid,
    queryFn: async () => (await unwrap(fetchShareSuggestions())).people,
  });
  return q.data ?? [];
}

export function useAddPerson(id: string) {
  const uid = useUid();
  const qc = useQueryClient();
  return useMutation<ShareCall<AddPersonResult>, never, string>({
    mutationFn: (email) => addPersonCall(id, email),
    onSuccess: (r) => {
      if (!r.ok || r.value.status !== "added") return;
      const person = r.value.person;
      qc.setQueryData<ShareState>(shareKeys.state(uid, id), (s) =>
        s && !s.people.some((p) => p.userId === person.userId)
          ? { ...s, people: [...s.people, person] }
          : s
      );
      void qc.invalidateQueries({ queryKey: shareKeys.marks(uid) });
      void qc.invalidateQueries({ queryKey: shareKeys.suggestions(uid) });
    },
    // The server's answer wins after any change: it corrects whatever a rollback got wrong.
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: shareKeys.state(uid, id) }),
  });
}

export function useRemovePerson(id: string) {
  const uid = useUid();
  const qc = useQueryClient();
  return useMutation<
    ShareCall<{ removed: boolean }>,
    never,
    SharePerson,
    { before?: ShareState }
  >({
    mutationFn: (person) => removePersonCall(id, person.userId),
    onMutate: async (person) => {
      await qc.cancelQueries({ queryKey: shareKeys.state(uid, id) });
      const before = qc.getQueryData<ShareState>(shareKeys.state(uid, id));
      qc.setQueryData<ShareState>(shareKeys.state(uid, id), (s) =>
        s
          ? { ...s, people: s.people.filter((p) => p.userId !== person.userId) }
          : s
      );
      return { before };
    },
    onSuccess: (r, _person, ctx) => {
      if (!r.ok && ctx?.before)
        qc.setQueryData(shareKeys.state(uid, id), ctx.before);
      void qc.invalidateQueries({ queryKey: shareKeys.marks(uid) });
    },
    // The server's answer wins after any change: it corrects whatever a rollback got wrong.
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: shareKeys.state(uid, id) }),
  });
}

export function useSetGeneralAccess(id: string) {
  const uid = useUid();
  const qc = useQueryClient();
  return useMutation<
    ShareCall<{ general: GeneralAccess }>,
    never,
    GeneralAccess,
    { before?: ShareState }
  >({
    mutationFn: (general) => setGeneralAccessCall(id, general),
    onMutate: async (general) => {
      await qc.cancelQueries({ queryKey: shareKeys.state(uid, id) });
      const before = qc.getQueryData<ShareState>(shareKeys.state(uid, id));
      qc.setQueryData<ShareState>(shareKeys.state(uid, id), (s) =>
        s ? { ...s, general } : s
      );
      return { before };
    },
    onSuccess: (r, _general, ctx) => {
      if (!r.ok && ctx?.before) {
        qc.setQueryData(shareKeys.state(uid, id), ctx.before);
        return;
      }
      // Files and Creations show the access they read from their own lists.
      void qc.invalidateQueries({ queryKey: ["files-listing"] });
      void qc.invalidateQueries({ queryKey: ["task-artifacts"] });
      void qc.invalidateQueries({ queryKey: ["user-artifacts"] });
    },
    // The server's answer wins after any change: it corrects whatever a rollback got wrong.
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: shareKeys.state(uid, id) }),
  });
}
